<template>
  <div class="skill-page">
    <div class="toolbar">
      <a-input-search
        v-model:value="query.keyword"
        class="search-input"
        placeholder="搜索技能名称、标识或简介"
        allow-clear
        @search="loadSkills(1)"
      />
      <a-select
        v-model:value="query.category"
        class="filter-item"
        placeholder="分类"
        allow-clear
        @change="loadSkills(1)"
      >
        <a-select-option v-for="item in categories" :key="item.value" :value="item.value">
          {{ item.label }}
        </a-select-option>
      </a-select>
      <a-select
        v-model:value="query.agent"
        class="filter-item"
        placeholder="智能体"
        allow-clear
        @change="loadSkills(1)"
      >
        <a-select-option v-for="item in agents" :key="item.value" :value="item.value">
          {{ item.label }}
        </a-select-option>
      </a-select>
      <a-button type="primary" @click="loadSkills(1)">
        <template #icon><SearchOutlined /></template>
        查询
      </a-button>
    </div>

    <a-table
      :columns="columns"
      :data-source="skills"
      :loading="loading"
      :pagination="pagination"
      row-key="id"
      @change="handleTableChange"
    >
      <template #name="{ record }">
        <div class="skill-name-cell">
          <a @click="openDetail(record)">{{ record.name }}</a>
          <div class="summary">{{ record.summary }}</div>
        </div>
      </template>
      <template #category="{ record }">
        <a-tag>{{ categoryText(record.category) }}</a-tag>
      </template>
      <template #agents="{ record }">
        <a-tag v-for="agent in record.supportedAgents || []" :key="agent" color="blue">
          {{ agentText(agent) }}
        </a-tag>
      </template>
      <template #size="{ record }">
        {{ formatSize(record.packageSize) }}
      </template>
      <template #action="{ record }">
        <a-space>
          <a-button size="small" type="primary" @click="openDetail(record)">
            安装
          </a-button>
          <a-button size="small" :href="downloadUrl(record)" target="_blank">
            <template #icon><DownloadOutlined /></template>
            下载
          </a-button>
        </a-space>
      </template>
    </a-table>

    <a-drawer
      v-model:visible="detailDrawerVisible"
      class="skill-detail-drawer"
      title="技能详情"
      width="1080"
      :mask="false"
      :destroy-on-close="true"
    >
      <skill-detail v-if="detailId" :skill-id="detailId" />
    </a-drawer>
  </div>
</template>

<script>
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons-vue';
import { downloadSkill, getSkillList } from '@/api/skill';
import SkillDetail from './detail.vue';

export default {
  components: { DownloadOutlined, SearchOutlined, SkillDetail },
  data() {
    return {
      loading: false,
      detailHistoryPushed: false,
      skills: [],
      query: {
        keyword: '',
        category: undefined,
        agent: undefined,
      },
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showTotal: total => `共 ${total} 个`,
      },
      categories: [
        { label: '通用', value: 'general' },
        { label: '投放查询', value: 'advertising' },
        { label: '数据分析', value: 'data' },
        { label: '内容创作', value: 'content' },
        { label: '办公效率', value: 'productivity' },
      ],
      agents: [
        { label: 'Codex', value: 'codex' },
        { label: 'Claude', value: 'claude' },
        { label: 'OpenClaw', value: 'openclaw' },
        { label: 'WorkBuddy', value: 'workbuddy' },
        { label: 'Hermes', value: 'hermes' },
      ],
      columns: [
        { title: '技能', dataIndex: 'name', key: 'name', width: 300, slots: { customRender: 'name' } },
        { title: '分类', dataIndex: 'category', key: 'category', width: 110, slots: { customRender: 'category' } },
        { title: '最新版本', dataIndex: 'latestVersion', key: 'latestVersion', width: 100 },
        { title: '支持平台', dataIndex: 'supportedAgents', key: 'agents', width: 220, slots: { customRender: 'agents' } },
        { title: '大小', dataIndex: 'packageSize', key: 'size', width: 100, slots: { customRender: 'size' } },
        { title: '更新时间', dataIndex: 'updatedAt', key: 'updatedAt', width: 170 },
        { title: '下载次数', dataIndex: 'downloadCount', key: 'downloadCount', width: 100 },
        { title: '操作', key: 'action', width: 180, slots: { customRender: 'action' } },
      ],
    };
  },
  computed: {
    detailId() {
      const detail = this.$route.query.detail;
      if (!detail) return '';
      return String(Array.isArray(detail) ? detail[0] : detail);
    },
    detailDrawerVisible: {
      get() {
        return Boolean(this.detailId);
      },
      set(value) {
        if (!value) this.closeDetail();
      },
    },
  },
  watch: {
    '$route.query.detail'(value) {
      if (!value) this.detailHistoryPushed = false;
    },
  },
  mounted() {
    this.loadSkills();
  },
  methods: {
    async loadSkills(page) {
      if (page) this.pagination.current = page;
      this.loading = true;
      try {
        const params = {
          page: this.pagination.current,
          limit: this.pagination.pageSize,
          ...this.query,
        };
        const res = await getSkillList(params);
        if (res.code === 0) {
          this.skills = res.page?.list || [];
          this.pagination.total = res.page?.totalCount || 0;
        }
      } finally {
        this.loading = false;
      }
    },
    handleTableChange(pagination) {
      this.pagination.current = pagination.current;
      this.pagination.pageSize = pagination.pageSize;
      this.loadSkills();
    },
    async openDetail(record) {
      const detailId = String(record.id);
      if (this.detailId === detailId) return;

      const alreadyOpened = Boolean(this.detailId);
      try {
        await this.$router[alreadyOpened ? 'replace' : 'push']({
          query: { ...this.$route.query, detail: detailId },
        });
        if (!alreadyOpened) this.detailHistoryPushed = true;
      } catch (error) {
        // Vue Router may reject a repeated navigation; the drawer state follows the URL.
      }
    },
    closeDetail() {
      if (!this.detailId) return;

      if (this.detailHistoryPushed) {
        this.detailHistoryPushed = false;
        this.$router.back();
        return;
      }

      const query = { ...this.$route.query };
      delete query.detail;
      this.$router.replace({ query });
    },
    downloadUrl(record) {
      return downloadSkill(record.latestVersionId);
    },
    categoryText(value) {
      return this.categories.find(item => item.value === value)?.label || value || '通用';
    },
    agentText(value) {
      return this.agents.find(item => item.value === value)?.label || value;
    },
    formatSize(value) {
      if (!value) return '-';
      if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
      return `${(value / 1024 / 1024).toFixed(1)} MB`;
    },
  },
};
</script>

<style lang="less" scoped>
.skill-page {
  padding: 20px;
  background: #fff;
  min-height: 100%;
}

.toolbar {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.search-input {
  width: 320px;
}

.filter-item {
  width: 150px;
}

.skill-name-cell {
  .summary {
    margin-top: 4px;
    color: #6b7280;
    font-size: 12px;
    line-height: 18px;
  }
}
</style>
